/**
 * Thin wrapper around MediaRecorder + Web Audio AnalyserNode.
 *
 * - `createRecorder` owns the MediaRecorder lifecycle for a single take.
 * - The AnalyserNode lets the UI draw a simple single-bar level meter
 *   without pulling in a charting library.
 *
 * The underlying MediaStream is *not* created here — the parent component
 * owns `getUserMedia` so we can reuse the permission across questions.
 */

export interface RecorderHandle {
  start: () => void;
  /** Stops the recorder and resolves with the accumulated blob. */
  stop: () => Promise<Blob>;
  /** Cancels recording, throwing away any captured audio. */
  cancel: () => void;
  /** Peak 0..1 over the last analysed frame. Safe to call any time. */
  readLevel: () => number;
  /** True while the MediaRecorder is recording. */
  isRecording: () => boolean;
  /** ms elapsed since `start` (0 before start). */
  elapsedMs: () => number;
  /** Free the Web Audio nodes attached to the stream. */
  dispose: () => void;
}

/**
 * Ordered best-to-worst MIME candidates. Safari lacks webm support, hence
 * the mp4/AAC fallback.
 */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

export function pickMimeType(preferred?: string): string | undefined {
  if (
    preferred &&
    typeof MediaRecorder !== 'undefined' &&
    MediaRecorder.isTypeSupported(preferred)
  ) {
    return preferred;
  }
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const mime of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return undefined;
}

export async function requestMicStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Microphone API is not available in this browser.');
  }
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
  });
}

/**
 * Stops all tracks, releasing the mic indicator in the browser chrome.
 */
export function releaseStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // ignore
    }
  }
}

export function createRecorder(
  stream: MediaStream,
  opts: { mimeType?: string } = {},
): RecorderHandle {
  const mimeType = pickMimeType(opts.mimeType);
  const recOptions: MediaRecorderOptions = mimeType ? { mimeType } : {};
  const recorder = new MediaRecorder(stream, recOptions);
  const chunks: Blob[] = [];
  let startTs = 0;

  recorder.addEventListener('dataavailable', (ev) => {
    if (ev.data && ev.data.size > 0) chunks.push(ev.data);
  });

  // Level meter — shared AudioContext per recorder.
  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  const audioCtx = AudioCtx ? new AudioCtx() : null;
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  // Note: lib.dom in TS 5.6+ types getByteFrequencyData as Uint8Array<ArrayBuffer>
  // (not the generic Uint8Array<ArrayBufferLike>). Constructing from an explicit
  // ArrayBuffer pins the type so the call type-checks.
  let freqBuf: Uint8Array<ArrayBuffer> | null = null;

  if (audioCtx) {
    try {
      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      freqBuf = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
    } catch {
      analyser = null;
    }
  }

  function readLevel(): number {
    if (!analyser || !freqBuf) return 0;
    analyser.getByteFrequencyData(freqBuf);
    let sum = 0;
    for (let i = 0; i < freqBuf.length; i += 1) sum += freqBuf[i]!;
    const avg = sum / freqBuf.length / 255;
    // Emphasise speech band a bit — gives a more satisfying visual.
    return Math.min(1, avg * 1.6);
  }

  return {
    start: () => {
      if (recorder.state === 'recording') return;
      chunks.length = 0;
      startTs = performance.now();
      // Resume audio context in case it started suspended.
      if (audioCtx && audioCtx.state === 'suspended') {
        void audioCtx.resume();
      }
      recorder.start();
    },
    stop: () =>
      new Promise<Blob>((resolve) => {
        if (recorder.state !== 'recording') {
          resolve(new Blob(chunks, { type: mimeType ?? 'audio/webm' }));
          return;
        }
        recorder.addEventListener(
          'stop',
          () => {
            const blob = new Blob(chunks, {
              type: mimeType ?? recorder.mimeType ?? 'audio/webm',
            });
            resolve(blob);
          },
          { once: true },
        );
        recorder.stop();
      }),
    cancel: () => {
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch {
          // ignore
        }
      }
      chunks.length = 0;
    },
    readLevel,
    isRecording: () => recorder.state === 'recording',
    elapsedMs: () => (startTs === 0 ? 0 : performance.now() - startTs),
    dispose: () => {
      try {
        if (source) source.disconnect();
        if (analyser) analyser.disconnect();
      } catch {
        // ignore
      }
      if (audioCtx && audioCtx.state !== 'closed') {
        void audioCtx.close();
      }
    },
  };
}
